<template>
   <div class="view-clients-page">
      <!-- ── Page Header ── -->
      <div class="ms-page-header">
         <div class="ms-page-header__content d-flex justify-content-between align-items-center flex-wrap gap-3 w-100">
            <div class="d-flex align-items-center gap-3">
               <div class="ms-page-header__icon">
                  <i class="fas fa-users-rectangle"></i>
               </div>
               <div>
                  <p class="ms-page-header__breadcrumb">Organisations <span class="ms-breadcrumb-sep">/</span> All Organisations</p>
                  <h1 class="ms-page-header__title">All Organisations</h1>
                  <p class="ms-page-header__subtitle">Manage organisation profiles, contracts, and proposals</p>
               </div>
            </div>
         </div>
      </div>

      <LoadingPage v-if="isLoading"></LoadingPage>

      <!-- ── Organisations Card ── -->
      <div class="ms-card mb-4">
         <div class="org-tabs-container">
            <div class="org-tabs-nav">
               <button v-for="tab in ['Partner', 'Lead', 'Client', 'Prospect', 'Supplier', 'Merged', 'Deactivated']" 
                       :key="tab" 
                       class="org-tab-btn" 
                       :class="{ active: activeTab === tab }" 
                       @click="selectTab(tab)">
                  {{ tab }}
               </button>
            </div>

            <!-- ── Search & Navigation Container ── -->
            <div class="d-flex align-items-center gap-3 flex-wrap py-2">
               <!-- ── Custom Autocomplete Search Bar inside Table Header ── -->
               <div class="header-search-container m-0">
                  <div class="header-search-input-wrapper">
                     <i class="fas fa-search search-icon"></i>
                     <input 
                        type="text" 
                        v-model="searchQuery" 
                        class="header-search-input" 
                        placeholder="Search across all organisations..." 
                        @focus="showSearchDropdown = true"
                        @input="onSearchInput"
                     />
                     <button v-if="searchQuery" class="clear-search-btn" @click="clearSearchQuery" type="button">
                        <i class="fas fa-times"></i>
                     </button>
                  </div>
                  
                  <!-- Suggestions Dropdown overlay -->
                  <div v-if="showSearchDropdown && searchQuery && searchDropdownList.length > 0" class="header-search-dropdown">
                     <div 
                        v-for="org in searchDropdownList" 
                        :key="org.id || org.uuid" 
                        class="header-search-dropdown-item"
                        @mousedown="selectOrgFromDropdown(org)"
                     >
                        <div class="d-flex align-items-center justify-content-between">
                           <span class="org-name-text">{{ org.organisation_name }}</span>
                           <div class="d-flex align-items-center gap-1">
                              <span v-if="org.is_merged" class="org-merged-badge-sm" :title="'Merged into: ' + (org.merged_into_name || 'Primary Organisation')">
                                 <i class="fas fa-code-branch me-1"></i> Merged to {{ org.merged_into_name || 'Primary Organisation' }}
                              </span>
                              <span v-if="isPartner(org) && getOrgClassification(org) !== 'Partner'" class="org-partner-badge-sm">Partner</span>
                              <span v-if="isSupplier(org) && getOrgClassification(org) !== 'Supplier'" class="org-supplier-badge-sm">Supplier</span>
                              <span class="org-type-badge" :class="'org-type-badge--' + (getOrgClassification(org) || '').toLowerCase()">{{ getOrgClassification(org) }}</span>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>

               <!-- Filter Settings Button -->
               <div class="d-flex align-items-center ms-2 me-2">
                  <button @click="showFilterSettingsModal = true" class="btn-ghost-secondary d-flex align-items-center gap-2" :class="{ 'btn-filter-active': hasActiveCustomFilters }" style="padding: 6px 14px; font-size: 13.5px; height: 38px;">
                     <i class="fa-solid fa-sliders"></i>
                     Filter Settings
                     <span v-if="activeFilterCount > 0" class="filter-count-badge">{{ activeFilterCount }}</span>
                  </button>
               </div>

               <!-- ── Page Navigation ── -->
               <div class="ms-pagination">
                  <button @click="prevPage" :disabled="page === 1" class="ms-page-btn">
                     <i class="fas fa-chevron-left"></i>
                  </button>
                  <span class="ms-page-label">
                     <strong>{{ paginationLabel }}</strong> of {{ filteredClients.length }}
                  </span>
                  <button @click="nextPage" :disabled="page >= totalPages" class="ms-page-btn">
                     <i class="fas fa-chevron-right"></i>
                  </button>
               </div>
            </div>
         </div>

         <div class="ms-table-wrap">
            <table class="ms-table">
               <thead>
                  <tr>
                     <th @click="toggleSort('name')" style="cursor: pointer; user-select: none;">
                        Organisation
                        <i class="fas ms-2" :class="getSortIcon('name')"></i>
                     </th>
                     <th @click="toggleSort('date')" style="cursor: pointer; user-select: none;">
                        Date Created
                        <i class="fas ms-2" :class="getSortIcon('date')"></i>
                     </th>
                     <th class="text-end">Actions</th>
                  </tr>
               </thead>
               <tbody>
                  <tr v-if="paginatedClients.length === 0">
                     <td colspan="3" class="text-center py-4 text-muted">
                        No organisation records found.
                     </td>
                  </tr>
                  <tr v-for="client in paginatedClients" :key="client.uuid || client.id" class="client-row" @click="editClient(client)">
                     <td class="client-name-cell">
                        <div class="avatar-circle me-3" :style="{ backgroundColor: getColorFromName(client.organisation_name) }">
                           {{ getInitials(client.organisation_name) }}
                        </div>
                        <div class="d-flex flex-column gap-1">
                            <div class="fw-bold text-dark d-flex align-items-center gap-2 flex-wrap">
                               <span>{{ client.organisation_name }}</span>

                               <!-- Partner Label -->
                               <span v-if="isPartner(client)" class="org-partner-badge" title="Partner">
                                  Partner
                               </span>

                               <!-- Supplier Label -->
                               <span v-if="isSupplier(client)" class="org-supplier-badge" title="Supplier">
                                  Supplier
                               </span>

                               <!-- Owner / Vertex Lead Label -->
                               <span v-if="getVertexLead(client)" class="org-owner-badge" :title="'Owner (Vertex Lead): ' + getVertexLead(client)">
                                  <i class="fas fa-user-tie"></i> Vertex Lead: {{ getVertexLead(client) }}
                               </span>

                               <!-- Auth Status Badges -->
                               <!-- 1. Auth Synced (created/synced from Auth cron) -->
                               <span v-if="client.is_auth_synced" class="auth-synced-badge" :title="'Synced with Auth: ' + (client.auth_organisation_name || client.organisation_name)">
                                  <i class="fas fa-sync-alt"></i> Auth Synced
                               </span>
                               <!-- 2. Manually Linked to an Auth Organisation -->
                               <span v-else-if="client.has_auth_organisation == 1" class="auth-linked-badge" :title="'Manually Linked to Auth: ' + (client.auth_organisation_name || 'Auth Org #' + client.auth_organisation_id)">
                                  <i class="fas fa-link"></i> Manually Linked to {{ client.auth_organisation_name || ('Auth Org #' + client.auth_organisation_id) }}
                               </span>
                               <!-- 3. Marked for Creation in Auth -->
                               <span v-else-if="client.has_auth_organisation == 2" class="lead-marked-badge" title="Marked for creation in Auth">
                                  <i class="fas fa-clock"></i> Marked for Creation
                               </span>

                               <!-- Test Data & Merged Badges -->
                               <span v-if="client.is_merged" class="org-merged-badge" :title="'Merged into: ' + (client.merged_into_name || 'Primary Organisation')">
                                  <i class="fas fa-code-branch me-1"></i> Merged to {{ client.merged_into_name || 'Primary Organisation' }}
                               </span>
                               <span v-if="client.is_test_data" class="org-test-badge">Test Data</span>
                            </div>
                            <small v-if="getFormerName(client)" class="text-muted fst-italic" style="font-size: 0.73rem;" :title="'Former Organisation Name: ' + getFormerName(client)">
                               <i class="fa-solid fa-history me-1"></i> Formerly: {{ getFormerName(client) }}
                            </small>
                            <div class="text-muted small d-flex align-items-center gap-2 flex-wrap">
                               <span>{{ client.email || 'No email associated' }}</span>
                            </div>
                        </div>
                     </td>
                     <td>{{ formatDate(client.created_at || client.date_created) }}</td>
                     <td class="text-end">
                        <div class="d-flex justify-content-end align-items-center gap-2">
                           <template v-if="activeTab === 'Deactivated'">
                              <button class="action-btn-circle action-btn-circle--restore" title="Restore Organisation" @click.stop="restoreOrganisation(client)">
                                 <i class="fas fa-undo"></i>
                              </button>
                           </template>
                           <template v-else>
                              <!-- View Proposals Button -->
                              <button class="action-btn-circle action-btn-circle--proposals" 
                                      title="View Proposals" 
                                      @click.stop="viewProposals(client)">
                                 <i class="fas fa-file-invoice"></i>
                              </button>

                              <!-- View Contracts Button -->
                              <button class="action-btn-circle action-btn-circle--contracts" 
                                      title="View Contracts" 
                                      @click.stop="viewContracts(client)">
                                 <i class="fas fa-file-signature"></i>
                              </button>

                              <!-- Edit Button -->
                              <button class="action-btn-circle action-btn-circle--edit" 
                                      title="Edit Client" 
                                      @click.stop="editClient(client)">
                                 <i class="fas fa-pen"></i>
                              </button>

                              <!-- Open in Auth Button -->
                              <button class="action-btn-circle action-btn-circle--open-auth" 
                                      :title="client.has_auth_organisation == 1 ? 'Open Organisation in Auth' : 'Only available for linked organisations'" 
                                      :disabled="client.has_auth_organisation != 1"
                                      @click.stop="client.has_auth_organisation == 1 ? openInAuth(client) : null">
                                 <i class="fas fa-external-link-alt"></i>
                              </button>

                              <!-- Open in Xero Button -->
                              <button class="action-btn-circle action-btn-circle--open-xero" 
                                      :title="client.has_auth_organisation == 1 ? 'Open Organisation in Xero' : 'Only available for linked organisations'" 
                                      :disabled="client.has_auth_organisation != 1"
                                      @click.stop="client.has_auth_organisation == 1 ? openInXero(client) : null">
                                 <i class="fas fa-file-invoice-dollar"></i>
                              </button>

                              <!-- Manual Sync from Auth Button -->
                              <button class="action-btn-circle action-btn-circle--sync" 
                                      :title="client.has_auth_organisation != 1 ? 'Only available for linked organisations' : (clientSyncing[client.auth_organisation_id] ? 'Syncing...' : 'Sync from Auth')" 
                                      :disabled="client.has_auth_organisation != 1 || clientSyncing[client.auth_organisation_id]"
                                      @click.stop="client.has_auth_organisation == 1 ? manualSyncFromAuth(client) : null">
                                 <i class="fas" :class="client.has_auth_organisation == 1 && clientSyncing[client.auth_organisation_id] ? 'fa-spinner fa-spin' : 'fa-sync-alt'"></i>
                              </button>

                               <!-- Mark/Unmark Auth Creation Button -->
                               <button v-if="client.has_auth_organisation == 0 || client.has_auth_organisation == null" 
                                       class="action-btn-circle action-btn-circle--mark" 
                                       title="Mark for Auth Creation" 
                                       @click.stop="openMarkAuthModal(client)">
                                  <i class="fas fa-clock"></i>
                               </button>
                               <button v-else-if="client.has_auth_organisation == 2"
                                       class="action-btn-circle action-btn-circle--unmark"
                                       title="Unmark from Auth Creation"
                                       @click.stop="openUnmarkAuthModal(client)">
                                  <i class="fas fa-times"></i>
                               </button>

                               <!-- Merge / Unmerge Organisation Button -->
                               <button v-if="!client.is_merged" 
                                       class="action-btn-circle action-btn-circle--merge" 
                                       title="Merge Organisation" 
                                       @click.stop="openMergeModal(client)">
                                  <i class="fas fa-code-branch"></i>
                               </button>
                               <button v-else-if="client.is_merged" 
                                       class="action-btn-circle action-btn-circle--unmerge" 
                                       title="Unmerge Organisation" 
                                       @click.stop="openUnmergeModal(client)">
                                  <i class="fas fa-undo"></i>
                               </button>
                           </template>
                        </div>
                     </td>
                  </tr>
               </tbody>
            </table>
         </div>
      </div>


      <!-- ── Contracts Modal ── -->
      <div v-if="showContractsModal" class="modal-backdrop-custom" @click.self="showContractsModal = false">
         <div class="modal-content-custom" style="max-width: 1000px;">
            <button @click="showContractsModal = false" class="close-button-custom">&times;</button>
            <h5 class="modal-title-custom">
               <i class="fas fa-file-signature me-2 text-info"></i>Contracts for {{ selectedClientForModal.organisation_name }}
            </h5>
            <hr>
            <div class="modal-body-custom">
               <div v-if="selectedClientForModal && selectedClientForModal.is_merged" class="alert alert-warning d-flex align-items-center gap-2 mb-3 py-2 px-3 small border-0" style="background-color: #fffbeb; color: #92400e; border-left: 4px solid #f59e0b !important; border-radius: 6px;">
                  <i class="fas fa-code-branch text-warning"></i>
                  <div>
                     <strong>Historical trailing:</strong> This organisation was merged into <strong>{{ selectedClientForModal.merged_into_name || 'Primary Organisation' }}</strong>. These records are also managed under the primary organisation.
                  </div>
               </div>
               <div v-if="modalLoading" class="text-center py-4">
                  <div class="spinner-border text-info" role="status"></div>
                  <div class="mt-2 text-muted">Loading contracts...</div>
               </div>
               <div v-else-if="modalDataList.length === 0" class="text-center py-4 text-muted">
                  <i class="fas fa-folder-open fa-2x mb-2 text-light-muted"></i>
                  <p>No contracts associated with this client.</p>
               </div>
               <div v-else class="table-responsive">
                  <table class="table align-middle">
                     <thead>
                        <tr>
                           <th>Contract Title</th>
                           <th class="text-nowrap">Proposal</th>
                           <th class="text-nowrap">Status</th>
                           <th class="text-nowrap">Date</th>
                           <th class="text-end text-nowrap">Actions</th>
                        </tr>
                     </thead>
                     <tbody>
                        <tr v-for="contract in modalDataList" :key="contract.id">
                           <td>{{ contract.contract_title || 'Unnamed Contract' }}</td>
                           <td>
                              <span v-if="contract.proposal" class="small">
                                 <a href="#" @click.prevent="previewProposal(contract.proposal)" class="text-info text-decoration-none d-inline-flex align-items-center modal-link-highlight" style="cursor: pointer;" title="Click to view proposal landing page">
                                    <i class="fas fa-file-invoice me-1"></i>
                                    {{ contract.proposal.proposal_title || 'Proposal #' + contract.proposal.id }}
                                 </a>
                              </span>
                              <span v-else class="text-muted small">-</span>
                           </td>
                           <td>
                              <span class="badge" :style="{ backgroundColor: getContractStatusColor(contract.contract_status) }">
                                 {{ getContractStatusName(contract.contract_status) }}
                              </span>
                           </td>
                           <td class="text-nowrap">
                              <div v-if="contract.contract_status === 4">
                                 <span class="text-muted small d-block" style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px;">Executed on</span>
                                 {{ formatDate(contract.accepted_at || contract.created_at) }}
                              </div>
                              <div v-else>
                                 <span class="text-muted small d-block" style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px;">Created on</span>
                                 {{ formatDate(contract.created_at) }}
                              </div>
                           </td>
                           <td class="text-end">
                              <div class="d-flex justify-content-end gap-2">
                                 <button class="action-btn-circle action-btn-circle--preview" title="View client contract" @click="previewContract(contract)">
                                    <i class="fas fa-eye"></i>
                                 </button>
                                 <button v-if="contract.contract_status !== 4" class="action-btn-circle action-btn-circle--edit" title="Edit/View" @click="editContract(contract)">
                                    <i class="fas fa-pen"></i>
                                 </button>
                              </div>
                           </td>
                        </tr>
                     </tbody>
                  </table>
               </div>
            </div>
         </div>
      </div>

      <!-- ── Proposals Modal ── -->
      <div v-if="showProposalsModal" class="modal-backdrop-custom" @click.self="showProposalsModal = false">
         <div class="modal-content-custom" style="max-width: 1000px;">
            <button @click="showProposalsModal = false" class="close-button-custom">&times;</button>
            <h5 class="modal-title-custom">
               <i class="fas fa-file-invoice me-2 text-info"></i>Proposals for {{ selectedClientForModal.organisation_name }}
            </h5>
            <hr>
            <div class="modal-body-custom">
               <div v-if="selectedClientForModal && selectedClientForModal.is_merged" class="alert alert-warning d-flex align-items-center gap-2 mb-3 py-2 px-3 small border-0" style="background-color: #fffbeb; color: #92400e; border-left: 4px solid #f59e0b !important; border-radius: 6px;">
                  <i class="fas fa-code-branch text-warning"></i>
                  <div>
                     <strong>Historical trailing:</strong> This organisation was merged into <strong>{{ selectedClientForModal.merged_into_name || 'Primary Organisation' }}</strong>. These records are also managed under the primary organisation.
                  </div>
               </div>
               <div v-if="modalLoading" class="text-center py-4">
                  <div class="spinner-border text-info" role="status"></div>
                  <div class="mt-2 text-muted">Loading proposals...</div>
               </div>
               <div v-else-if="modalDataList.length === 0" class="text-center py-4 text-muted">
                  <i class="fas fa-folder-open fa-2x mb-2 text-light-muted"></i>
                  <p>No proposals associated with this client.</p>
               </div>
               <div v-else class="table-responsive">
                  <table class="table align-middle">
                     <thead>
                        <tr>
                           <th>Proposal Name</th>
                           <th>Status</th>
                           <th>Date</th>
                           <th class="text-end">Actions</th>
                        </tr>
                     </thead>
                     <tbody>
                        <tr v-for="proposal in modalDataList" :key="proposal.id">
                           <td>{{ proposal.proposal_title || 'Proposal #' + proposal.id }}</td>
                           <td>
                              <span class="badge" :style="{ backgroundColor: getProposalStatusColor(proposal.proposal_status) }">
                                 {{ getProposalStatusName(proposal.proposal_status) }}
                              </span>
                           </td>
                           <td class="text-nowrap">
                              <div v-if="proposal.proposal_status === 3 || proposal.proposal_status === 6">
                                 <span class="text-muted small d-block" style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px;">Accepted on</span>
                                 {{ formatDate(proposal.accepted_at || proposal.created_at) }}
                              </div>
                              <div v-else-if="proposal.proposal_status === 4">
                                 <span class="text-muted small d-block" style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px;">Declined on</span>
                                 {{ formatDate(proposal.declined_at || proposal.created_at) }}
                              </div>
                              <div v-else>
                                 <span class="text-muted small d-block" style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px;">Created on</span>
                                 {{ formatDate(proposal.created_at) }}
                              </div>
                           </td>
                           <td class="text-end">
                              <div class="d-flex justify-content-end gap-2">
                                 <button class="action-btn-circle action-btn-circle--preview" title="View client proposal" @click="previewProposal(proposal)">
                                    <i class="fas fa-eye"></i>
                                 </button>
                                 <button v-if="proposal.proposal_status !== 3 && proposal.proposal_status !== 6" class="action-btn-circle action-btn-circle--edit" title="Edit/View" @click="editProposal(proposal)">
                                    <i class="fas fa-pen"></i>
                                 </button>
                              </div>
                           </td>
                        </tr>
                     </tbody>
                  </table>
               </div>
            </div>
         </div>
      </div>


      <!-- ── Filter Settings Modal ── -->
      <Transition name="lead-modal-fade">
         <div v-if="showFilterSettingsModal" class="lead-modal-overlay" @click.self="showFilterSettingsModal = false">
            <div class="lead-modal" style="max-width: 480px;">
               <div class="lead-modal__header">
                  <h5 class="lead-modal__title"><i class="fas fa-sliders"></i> Filter Settings</h5>
                  <button type="button" @click="showFilterSettingsModal = false" class="lead-modal__close">&times;</button>
               </div>
               <div class="lead-modal__divider"></div>
               <div class="lead-modal__body">
                  <!-- Exclude Test Data Switch -->
                  <div class="d-flex align-items-center justify-content-between mb-4 pb-3 border-bottom">
                     <div>
                        <h6 class="mb-0" style="font-weight: 600; color: #2D3748;">Exclude Test Data</h6>
                        <small class="text-muted">Hide organisations marked as test data</small>
                     </div>
                     <div class="form-check form-switch mb-0" style="padding-left: 0;">
                        <input v-model="excludeTestData" type="checkbox" class="form-check-input custom-switch-input mb-0" id="modalExcludeTestDataSwitch" style="cursor: pointer; width: 2.5em; height: 1.25em; margin-left: 0;">
                     </div>
                  </div>

                  <!-- Auth Linkage Filter Group -->
                  <div class="mb-4">
                     <label class="form-label fw-bold mb-2 text-dark" style="font-size: 0.88rem;">
                        <i class="fas fa-link me-1 text-info"></i> Auth Linkage Status
                     </label>
                     <div class="d-flex flex-column gap-2">
                        <label class="filter-modal-radio-card" :class="{ selected: filterAuthLinkage === '' }">
                           <input type="radio" value="" v-model="filterAuthLinkage" class="form-check-input me-2">
                           <span class="flex-grow-1">All Linkages</span>
                           <span class="badge bg-light text-secondary border">Default</span>
                        </label>
                        <label class="filter-modal-radio-card" :class="{ selected: filterAuthLinkage === 'linked' }">
                           <input type="radio" value="linked" v-model="filterAuthLinkage" class="form-check-input me-2">
                           <span class="flex-grow-1"><i class="fas fa-check-circle text-success me-1"></i> Linked Only</span>
                           <span class="badge bg-success text-white" style="font-size: 0.65rem; background-color: #059669 !important;">Linked</span>
                        </label>
                        <label class="filter-modal-radio-card" :class="{ selected: filterAuthLinkage === 'unlinked' }">
                           <input type="radio" value="unlinked" v-model="filterAuthLinkage" class="form-check-input me-2">
                           <span class="flex-grow-1"><i class="fas fa-times-circle text-danger me-1"></i> Unlinked Only</span>
                           <span class="badge bg-danger text-white" style="font-size: 0.65rem; background-color: #dc2626 !important;">Unlinked</span>
                        </label>
                        <label class="filter-modal-radio-card" :class="{ selected: filterAuthLinkage === 'marked' }">
                           <input type="radio" value="marked" v-model="filterAuthLinkage" class="form-check-input me-2">
                           <span class="flex-grow-1"><i class="fas fa-clock text-primary me-1"></i> Marked for Creation</span>
                           <span class="badge bg-primary text-white" style="font-size: 0.65rem; background-color: #1d4ed8 !important;">Marked</span>
                        </label>
                     </div>
                  </div>

                  <div class="lead-modal__actions d-flex gap-2 mt-4">
                     <button type="button" class="btn btn-outline-secondary w-50" @click="resetFilters">Reset</button>
                     <button type="button" class="lead-btn lead-btn--primary w-50" @click="showFilterSettingsModal = false">Apply Filters</button>
                  </div>
               </div>
            </div>
         </div>
      </Transition>

      <!-- ── Mark / Unmark Auth Creation Modal ── -->
      <Transition name="lead-modal-fade">
         <div v-if="authMarkModal.show" class="lead-modal-overlay" @click.self="closeAuthMarkModal">
            <div class="lead-modal" style="max-width: 460px;">
               <div class="lead-modal__header">
                  <h5 class="lead-modal__title">
                     <i :class="authMarkModal.type === 'mark' ? 'fas fa-clock text-info' : 'fas fa-times-circle text-danger'"></i>
                     {{ authMarkModal.type === 'mark' ? 'Mark for Auth Creation' : 'Unmark from Auth Creation' }}
                  </h5>
                  <button type="button" @click="closeAuthMarkModal" class="lead-modal__close">&times;</button>
               </div>
               <div class="lead-modal__divider"></div>
               <div class="lead-modal__body p-4 text-center">
                  <div class="mb-3">
                     <i v-if="authMarkModal.type === 'mark'" class="fas fa-clock fa-3x animate__animated animate__bounceIn" style="color: #00A0C1;"></i>
                     <i v-else class="fas fa-times-circle fa-3x text-danger animate__animated animate__bounceIn"></i>
                  </div>
                  <h6 class="fw-bold text-dark mb-2">
                     {{ authMarkModal.type === 'mark' ? 'Confirm Auth Creation Queue' : 'Confirm Unmark from Auth' }}
                  </h6>
                  <p class="text-muted mb-3" style="font-size: 0.95rem;">
                     Are you sure you want to {{ authMarkModal.type === 'mark' ? 'mark' : 'unmark' }}
                     <strong class="text-dark">{{ authMarkModal.orgName }}</strong>
                     {{ authMarkModal.type === 'mark' ? 'for creation in Auth?' : 'from Auth creation?' }}
                  </p>
                  <div v-if="authMarkModal.type === 'mark'" class="alert alert-info text-start small mb-0" style="background-color: #f0f9ff; border-color: #bae6fd; color: #0369a1;">
                     <i class="fas fa-info-circle me-1"></i>
                     This organisation will be queued for creation in Auth.
                  </div>
                  <div v-else class="alert alert-warning text-start small mb-0" style="background-color: #fffbeb; border-color: #fde68a; color: #92400e;">
                     <i class="fas fa-exclamation-triangle me-1"></i>
                     This organisation will be removed from the Auth creation queue and reverted to unlinked.
                  </div>
                  <div class="lead-modal__actions d-flex justify-content-center gap-2 mt-4">
                     <button type="button" class="btn btn-secondary px-4" @click="closeAuthMarkModal" :disabled="authMarkModal.isSubmitting">Cancel</button>
                     <button type="button" 
                             class="btn px-4 text-white" 
                             :class="authMarkModal.type === 'mark' ? 'btn-vertex-primary' : 'btn-danger'" 
                             @click="confirmAuthMarkAction" 
                             :disabled="authMarkModal.isSubmitting">
                        <span v-if="authMarkModal.isSubmitting">
                           <i class="fas fa-spinner fa-spin me-1"></i> Processing...
                        </span>
                        <span v-else>
                           <i :class="authMarkModal.type === 'mark' ? 'fas fa-check me-1' : 'fas fa-times me-1'"></i>
                           {{ authMarkModal.type === 'mark' ? 'Yes, Mark Organisation' : 'Yes, Unmark' }}
                        </span>
                     </button>
                  </div>
               </div>
            </div>
         </div>
      </Transition>

      <!-- ── Merge Organisation Modal ── -->
      <Transition name="lead-modal-fade">
         <div v-if="showMergeModal" class="lead-modal-overlay" @click.self="closeMergeModal">
            <div class="lead-modal" style="max-width: 680px;">
               <div class="lead-modal__header">
                  <h5 class="lead-modal__title">
                     <i class="fas fa-code-branch" style="color: #00768a;"></i> Merge Organisation
                  </h5>
                  <button type="button" @click="closeMergeModal" class="lead-modal__close">&times;</button>
               </div>
               <div class="lead-modal__divider"></div>
               <div class="lead-modal__body p-4">
                  <div class="alert d-flex align-items-start gap-2 small mb-3" style="background-color: #f0fdfa; border: 1px solid #99f6e4; border-left: 4px solid #00768a; color: #134e4a;">
                     <i class="fas fa-info-circle mt-1" style="color: #00768a;"></i>
                     <div>
                        Merging <strong>{{ selectedMergeSource ? (selectedMergeSource.organisation_name || selectedMergeSource.name) : '' }}</strong> will transfer all associated leads, contacts, and communication preferences into the selected destination organisation.
                     </div>
                  </div>

                  <div v-if="selectedMergeSource && (selectedMergeSource.has_auth_organisation == 1 || selectedMergeSource.is_auth_synced || (selectedMergeSource.auth_organisation_id && selectedMergeSource.auth_organisation_id > 0))" class="alert d-flex align-items-center gap-2 small py-2 px-3 mb-3 border-0 rounded-3" style="background-color: #eff6ff; color: #1e40af; border-left: 4px solid #3b82f6 !important;">
                     <i class="fas fa-link text-primary"></i>
                     <div>
                        <strong>Auth-Synced Organisation:</strong> Because this organisation is linked/synced with Auth, only other <strong>Auth-synced organisations</strong> can be selected as the destination.
                     </div>
                  </div>

                  <!-- Destination Search / Autocomplete -->
                  <div class="mb-3">
                     <label class="lead-form-label fw-bold">Destination Organisation <span class="text-danger">*</span></label>
                     <VueMultiselect
                        v-model="selectedMergeTarget"
                        :options="mergeSearchResults"
                        :searchable="true"
                        :loading="isSearchingMergeTargets"
                        :internal-search="true"
                        :clear-on-select="false"
                        :close-on-select="true"
                        :preserve-search="false"
                        placeholder="Search destination organisation..."
                        label="name"
                        track-by="id"
                        :show-no-results="true"
                        select-label=""
                        deselect-label=""
                        selected-label=""
                        class="merge-target-multiselect"
                     >
                        <template #option="props">
                           <div class="merge-multiselect-option d-flex flex-column py-1">
                              <div class="d-flex align-items-center gap-2">
                                 <span class="merge-option-name fw-bold">{{ props.option.name }}</span>
                                 <span v-if="props.option.has_auth_organisation == 1 || props.option.is_auth_synced || (props.option.auth_organisation_id && props.option.auth_organisation_id > 0)" class="merge-auth-badge badge bg-primary-subtle text-primary border border-primary-subtle flex-shrink-0" style="font-size: 0.65rem; padding: 2px 6px;">
                                    <i class="fas fa-link me-1"></i>Auth Synced
                                 </span>
                              </div>
                              <span class="merge-option-sub small">{{ props.option.abn || props.option.email || 'No ABN' }}</span>
                           </div>
                        </template>
                        <template #noResult>
                           <div class="text-muted small py-1">No matching organisations found</div>
                        </template>
                     </VueMultiselect>
                     <div class="form-text text-muted small mt-1" v-if="mergeSearchResults && mergeSearchResults.length > 0">
                        {{ mergeSearchResults.length }} organisation{{ mergeSearchResults.length === 1 ? '' : 's' }} available to merge into
                     </div>
                  </div>

                  <!-- Merge Reason -->
                  <div class="mb-3">
                     <label class="lead-form-label fw-bold">Reason for Merge (Optional)</label>
                     <textarea 
                        v-model="mergeReason" 
                        class="form-control" 
                        rows="2" 
                        placeholder="e.g. Company acquisition, duplicate record, restructuring..."
                     ></textarea>
                  </div>

                  <!-- Confirmation Checkbox -->
                  <div 
                     class="vertex-checkbox-card d-flex align-items-start gap-2 p-3 rounded-3 mb-3"
                     :class="{ 'vertex-checkbox-card--checked': mergeConfirmationChecked }"
                     @click="mergeConfirmationChecked = !mergeConfirmationChecked"
                  >
                     <div class="vertex-custom-checkbox pt-1" @click.stop>
                        <input 
                           class="form-check-input vertex-checkbox-input" 
                           type="checkbox" 
                           id="vcMergeConfirmCheckbox" 
                           v-model="mergeConfirmationChecked"
                        />
                     </div>
                     <label class="form-check-label small text-dark mb-0 user-select-none" for="vcMergeConfirmCheckbox" style="cursor: pointer; line-height: 1.45;" @click.stop="mergeConfirmationChecked = !mergeConfirmationChecked">
                        I understand that all active records from <strong>{{ selectedMergeSource ? (selectedMergeSource.organisation_name || selectedMergeSource.name) : '' }}</strong> will be reassigned to <strong>{{ selectedMergeTarget ? selectedMergeTarget.name : 'the destination organisation' }}</strong>.
                     </label>
                  </div>

                  <div class="lead-modal__actions d-flex justify-content-end gap-2 mt-4">
                     <button type="button" class="btn btn-secondary px-3" @click="closeMergeModal">Cancel</button>
                     <button 
                        type="button" 
                        class="btn btn-vertex-primary px-4" 
                        :disabled="!selectedMergeTarget || !mergeConfirmationChecked || isMerging" 
                        @click="confirmMerge"
                     >
                        <i v-if="isMerging" class="fas fa-spinner fa-spin me-1"></i>
                        <i v-else class="fas fa-code-branch me-1"></i>
                        {{ isMerging ? 'Merging...' : 'Confirm Merge' }}
                     </button>
                  </div>
               </div>
            </div>
         </div>
      </Transition>

      <!-- ── Unmerge Organisation Modal ── -->
      <Transition name="lead-modal-fade">
         <div v-if="showUnmergeModal" class="lead-modal-overlay" @click.self="closeUnmergeModal">
            <div class="lead-modal" style="max-width: 480px;">
               <div class="lead-modal__header">
                  <h5 class="lead-modal__title">
                     <i class="fas fa-undo" style="color: #00768a;"></i> Unmerge Organisation
                  </h5>
                  <button type="button" @click="closeUnmergeModal" class="lead-modal__close">&times;</button>
               </div>
               <div class="lead-modal__divider"></div>
               <div class="lead-modal__body p-4">
                  <div class="text-center mb-3">
                     <div class="d-inline-flex align-items-center justify-content-center rounded-circle mb-2" style="width: 56px; height: 56px; background-color: rgba(0, 118, 138, 0.1); color: #00768a;">
                        <i class="fas fa-undo fa-2x"></i>
                     </div>
                     <h6 class="fw-bold text-dark">Confirm Unmerge</h6>
                  </div>
                  <p class="small text-muted mb-3">
                     Are you sure you want to unmerge <strong>{{ unmergeTargetOrg ? (unmergeTargetOrg.organisation_name || unmergeTargetOrg.name) : '' }}</strong>? This will restore the organisation back to active status and return all originally transferred leads, contacts, and preferences.
                  </p>
                  <div class="lead-modal__actions d-flex justify-content-end gap-2 mt-4">
                     <button type="button" class="btn btn-secondary px-3" @click="closeUnmergeModal">Cancel</button>
                     <button 
                        type="button" 
                        class="btn btn-vertex-primary px-4" 
                        :disabled="isUnmerging" 
                        @click="confirmUnmerge"
                     >
                        <i v-if="isUnmerging" class="fas fa-spinner fa-spin me-1"></i>
                        <i v-else class="fas fa-undo me-1"></i>
                        {{ isUnmerging ? 'Unmerging...' : 'Confirm Unmerge' }}
                     </button>
                  </div>
               </div>
            </div>
         </div>
      </Transition>
   </div>
</template>

<script>
import moment from 'moment';
import LoadingPage from '@/components/Utilities/vertex-loading.vue';
import VueMultiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';
import '@/assets/scss/vue-multiselect.scss';

export default {
   name: 'ViewClients',
   components: {
      LoadingPage,
      VueMultiselect
   },
   data() {
      return {
         isLoading: false,
         clients: [],
         showFilterSettingsModal: false,
         searchQuery: '',
         showSearchDropdown: false,
         showFilterDropdown: false,
         filterAuthLinkage: '',
         excludeTestData: true,
         sortBy: 'name',
         page: 1,
         pageSize: 20,
         activeTab: 'Client',
         colorPalette: [
            '#FF5733', '#33FF57', '#3357FF', '#FF33A1', '#A133FF', '#33FFF2',
            '#FFC300', '#DAF7A6', '#C70039', '#900C3F', '#581845', '#FF8C00',
            '#4CAF50', '#2196F3', '#9C27B0', '#FFEB3B', '#FF9800', '#795548'
         ],

         // Modals
         showContractsModal: false,
         showProposalsModal: false,
         selectedClientForModal: null,
         modalLoading: false,
         modalDataList: [],
         clientSyncing: {},
         bdms: [],
         authMarkModal: {
            show: false,
            type: 'mark',
            org: null,
            orgName: '',
            isSubmitting: false
         },

         // Merge & Unmerge Modals
         showMergeModal: false,
         isMerging: false,
         selectedMergeSource: null,
         selectedMergeTarget: null,
         mergeSearchResults: [],
         isSearchingMergeTargets: false,
         mergeReason: '',
         mergeConfirmationChecked: false,

         showUnmergeModal: false,
         isUnmerging: false,
         unmergeTargetOrg: null
      };
   },
   created() {
      this.fetchClientsList();
      this.fetchBdms();
   },
   mounted() {
      document.addEventListener('click', this.handleDocumentClick);
   },
   beforeUnmount() {
      document.removeEventListener('click', this.handleDocumentClick);
   },
   watch: {
      excludeTestData() {
         this.page = 1;
      },
      filterAuthLinkage() {
         this.page = 1;
      }
   },
   computed: {
      activeFilterCount() {
         let count = 0;
         if (this.filterAuthLinkage !== '') count++;
         if (!this.excludeTestData) count++;
         return count;
      },
      hasActiveCustomFilters() {
         return this.activeFilterCount > 0;
      },
      searchList() {
         if (this.excludeTestData) {
            return this.clients.filter(c => !c.is_test_data);
         }
         return this.clients;
      },
      searchDropdownList() {
         if (!this.searchQuery) return [];
         const query = this.searchQuery.toLowerCase();
         let list = this.clients;
         if (this.excludeTestData) {
            list = list.filter(c => !c.is_test_data);
         }
         return list.filter(c => 
            (c.organisation_name && c.organisation_name.toLowerCase().includes(query)) ||
            (c.auth_organisation_name && c.auth_organisation_name.toLowerCase().includes(query)) ||
            (c.email && c.email.toLowerCase().includes(query))
         );
      },
      filterAuthLinkageLabel() {
         if (this.filterAuthLinkage === 'linked') return 'Linked';
         if (this.filterAuthLinkage === 'unlinked') return 'Unlinked';
         if (this.filterAuthLinkage === 'marked') return 'Marked';
         return 'All Linkages';
      },
      filteredClients() {
         let list = this.clients;

         // Filter by activeTab (classification or Merged)
         if (this.activeTab === 'Merged') {
            list = list.filter(c => c.is_merged);
         } else if (this.activeTab && this.activeTab !== 'Deactivated') {
            list = list.filter(c => !c.is_merged && c.classifications && c.classifications.includes(this.activeTab));
         }

         // Exclude Test Data Filter
         if (this.excludeTestData) {
            list = list.filter(c => !c.is_test_data);
         }

         // Search Filter
         if (this.searchQuery) {
            const query = this.searchQuery.toLowerCase();
             list = list.filter(c => 
                (c.organisation_name && c.organisation_name.toLowerCase().includes(query)) ||
                (c.auth_organisation_name && c.auth_organisation_name.toLowerCase().includes(query)) ||
                (c.email && c.email.toLowerCase().includes(query)) ||
                (c.former_names && c.former_names.some(fn => fn.toLowerCase().includes(query)))
             );
         }

         // Auth Linkage Filter
         if (this.filterAuthLinkage) {
            if (this.filterAuthLinkage === 'linked') {
               list = list.filter(c => c.has_auth_organisation == 1);
            } else if (this.filterAuthLinkage === 'unlinked') {
               list = list.filter(c => c.has_auth_organisation == 0 || c.has_auth_organisation == null);
            } else if (this.filterAuthLinkage === 'marked') {
               list = list.filter(c => c.has_auth_organisation == 2);
            }
         }

         // Sorting
         list = [...list];
         if (this.sortBy === 'name') {
            list.sort((a, b) => (a.organisation_name || '').localeCompare(b.organisation_name || ''));
         } else if (this.sortBy === 'name_desc') {
            list.sort((a, b) => (b.organisation_name || '').localeCompare(a.organisation_name || ''));
         } else if (this.sortBy === 'newest') {
            list.sort((a, b) => new Date(b.created_at || b.date_created || 0) - new Date(a.created_at || a.date_created || 0));
         } else if (this.sortBy === 'oldest') {
            list.sort((a, b) => new Date(a.created_at || a.date_created || 0) - new Date(b.created_at || b.date_created || 0));
         } else if (this.sortBy === 'linkage_asc') {
            list.sort((a, b) => (a.has_auth_organisation || 0) - (b.has_auth_organisation || 0));
         } else if (this.sortBy === 'linkage_desc') {
            list.sort((a, b) => (b.has_auth_organisation || 0) - (a.has_auth_organisation || 0));
         }

         return list;
      },
      paginatedClients() {
         const start = (this.page - 1) * this.pageSize;
         return this.filteredClients.slice(start, start + this.pageSize);
      },
      totalPages() {
         return Math.ceil(this.filteredClients.length / this.pageSize) || 1;
      },
      paginationLabel() {
         const total = this.filteredClients.length;
         if (total === 0) return '0 - 0';
         const start = Math.min((this.page - 1) * this.pageSize + 1, total);
         const end = Math.min(this.page * this.pageSize, total);
         return `${start}-${end}`;
      },
   },
   methods: {
      resetFilters() {
         this.filterAuthLinkage = '';
         this.excludeTestData = true;
         this.page = 1;
      },
      selectTab(tab) {
         this.activeTab = tab;
         this.page = 1;
         this.fetchClientsList();
      },
      async fetchClientsList() {
         this.isLoading = true;
         try {
            const onlyDeactivated = this.activeTab === 'Deactivated';
            const onlyMerged = this.activeTab === 'Merged';
            let url = `/organisations?pageSize=10000&page=1&excludeTestData=false`;
            if (onlyDeactivated) {
               url += '&onlyDeactivated=true';
            } else if (onlyMerged) {
               url += '&onlyMerged=true';
            }
            const response = await this.$axios.get(url);
            if (response.data && response.data.organisations) {
               this.clients = response.data.organisations.map(org => {
                  const classifications = org.classifications ? org.classifications.map(c => c.classification) : [];
                  let assignedUserName = '';
                  if (org.assigned_user) {
                     assignedUserName = org.assigned_user.name || `${org.assigned_user.first_name || ''} ${org.assigned_user.last_name || ''}`.trim() || org.assigned_user.email || '';
                  }
                  return {
                     uuid: org.lead_uuid || org.id,
                     lead_uuid: org.lead_uuid,
                     organisation_uuid: org.id,
                     organisation_name: org.name,
                     email: org.email,
                     created_at: org.created_at || org.date_created,
                     classifications: classifications,
                     is_partner: classifications.includes('Partner') || org.is_partner || false,
                     is_supplier: classifications.includes('Supplier') || org.is_supplier || false,
                     assigned_user_id: org.assigned_user_id,
                     assigned_user: org.assigned_user,
                     assigned_user_name: assignedUserName,
                     has_auth_organisation: org.has_auth_organisation,
                     auth_organisation_id: org.auth_organisation_id,
                     auth_organisation_uuid: org.auth_organisation_uuid,
                     auth_organisation_name: org.auth_organisation_name,
                     xero_contact_id: org.xero_contact_id,
                     is_auth_synced: org.is_auth_synced || false,
                     is_test_data: org.is_test_data || false,
                     is_merged: org.is_merged || false,
                     merged_into_uuid: org.merged_into_uuid || '',
                     merged_into_name: org.merged_into_name || '',
                     merged_at: org.merged_at || null,
                     merged_by_name: org.merged_by_name || '',
                     merge_reason: org.merge_reason || '',
                     original_organisation_name: org.original_organisation_name,
                     pre_link_snapshot: org.pre_link_snapshot,
                     former_names: org.former_names || [],
                  };
               });
            } else {
               this.clients = [];
            }
         } catch (error) {
            console.error('Error fetching clients as organisations:', error);
            this.$toast.error('Unable to fetch clients list from database.');
            this.clients = [];
         } finally {
            this.isLoading = false;
         }
      },
      async restoreOrganisation(client) {
         const uuid = client.organisation_uuid || client.uuid;
         if (!uuid) return;

         this.isLoading = true;
         try {
            const response = await this.$axios.put(`/organisations/${uuid}/restore`);
            if (response.data && response.data.success) {
               this.$toast.success('Organisation restored successfully.');
               await this.fetchClientsList();
            } else {
               this.$toast.error(response.data.message || 'Failed to restore organisation.');
            }
         } catch (error) {
            const msg = error.response && error.response.data && error.response.data.message
               ? error.response.data.message
               : 'Error restoring organisation';
            this.$toast.error(msg);
         } finally {
            this.isLoading = false;
         }
      },
      getColorFromName(name) {
         if (!name) {
            return '#6c757d';
         }
         let hash = 0;
         for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
         }
         const colorIndex = Math.abs(hash) % this.colorPalette.length;
         return this.colorPalette[colorIndex];
      },
      getInitials(name) {
         if (!name) return 'C';
         return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
      },
      formatDate(date) {
         if (!date) return '-';
         return moment(date).format('D-MMM-YYYY');
      },
      onSearch() {
         this.page = 1;
      },
      onSearchChange(query) {
         this.searchQuery = query || '';
         this.page = 1;
      },
      handleDocumentClick(e) {
         const container = this.$el.querySelector('.header-search-container');
         if (container && !container.contains(e.target)) {
            this.showSearchDropdown = false;
         }
         const filterWrapper = this.$el.querySelector('.header-filter-dropdown-wrapper');
         if (filterWrapper && !filterWrapper.contains(e.target)) {
            this.showFilterDropdown = false;
         }
      },
      getOrgClassification(org) {
         if (org && org.classifications && org.classifications.length > 0) {
            const primary = org.classifications.find(c => {
               const val = typeof c === 'string' ? c : (c.classification || '');
               return ['client', 'lead', 'prospect'].includes(val.toLowerCase());
            });
            if (primary) {
               return typeof primary === 'string' ? primary : primary.classification;
            }
            const first = org.classifications[0];
            return typeof first === 'string' ? first : (first.classification || 'Client');
         }
         return 'Client';
      },
      selectOrgFromDropdown(org) {
         this.showSearchDropdown = false;
         this.editClient(org);
      },
      clearSearchQuery() {
         this.searchQuery = '';
         this.showSearchDropdown = false;
         this.page = 1;
      },
      onSearchInput() {
         this.showSearchDropdown = true;
         this.page = 1;
      },
      getSortIcon(column) {
         if (column === 'name') {
            if (this.sortBy === 'name') return 'fa-sort-up text-info';
            if (this.sortBy === 'name_desc') return 'fa-sort-down text-info';
         } else if (column === 'date') {
            if (this.sortBy === 'oldest') return 'fa-sort-up text-info';
            if (this.sortBy === 'newest') return 'fa-sort-down text-info';
         } else if (column === 'linkage') {
            if (this.sortBy === 'linkage_asc') return 'fa-sort-up text-info';
            if (this.sortBy === 'linkage_desc') return 'fa-sort-down text-info';
         }
         return 'fa-sort text-muted';
      },
      toggleSort(column) {
         if (column === 'name') {
            this.sortBy = this.sortBy === 'name' ? 'name_desc' : 'name';
         } else if (column === 'date') {
            this.sortBy = this.sortBy === 'newest' ? 'oldest' : 'newest';
         } else if (column === 'linkage') {
            this.sortBy = this.sortBy === 'linkage_asc' ? 'linkage_desc' : 'linkage_asc';
         }
         this.page = 1;
      },
      prevPage() {
         if (this.page > 1) this.page--;
      },
      nextPage() {
         if (this.page < this.totalPages) this.page++;
      },
      toggleFilterDropdown() {
         this.showFilterDropdown = !this.showFilterDropdown;
      },
      selectFilter(value) {
         this.filterAuthLinkage = value;
         this.showFilterDropdown = false;
         this.page = 1;
      },

      // Modal Actions
      async viewContracts(client) {
         this.selectedClientForModal = client;
         this.showContractsModal = true;
         this.modalLoading = true;
         this.modalDataList = [];
         
         const orgId = client.organisation_uuid || client.uuid || client.id;
         const leadUuid = client.lead_uuid || (client.uuid !== client.organisation_uuid ? client.uuid : null);
         try {
            let response = null;
            if (orgId) {
               try {
                  response = await this.$axios.get(`/organisations/${orgId}/contracts`);
               } catch (e) {
                  // Fallback to lead endpoint
               }
            }
            if ((!response || !response.data || !response.data.vertex_contracts) && leadUuid) {
               response = await this.$axios.get(`/leads/${leadUuid}/contracts`);
            }
            if (response && response.data && response.data.vertex_contracts) {
               this.modalDataList = response.data.vertex_contracts;
            }
         } catch (err) {
            console.error('Error fetching contracts:', err);
            this.$toast.error('Could not load contracts for client.');
         }
         this.modalLoading = false;
      },

      async viewProposals(client) {
         this.selectedClientForModal = client;
         this.showProposalsModal = true;
         this.modalLoading = true;
         this.modalDataList = [];

         const orgId = client.organisation_uuid || client.uuid || client.id;
         const leadUuid = client.lead_uuid || (client.uuid !== client.organisation_uuid ? client.uuid : null);
         try {
            let response = null;
            if (orgId) {
               try {
                  response = await this.$axios.get(`/organisations/${orgId}/vertex-proposals`);
               } catch (e) {
                  // Fallback to lead endpoint
               }
            }
            if ((!response || !response.data || !response.data.vertex_proposals) && leadUuid) {
               response = await this.$axios.get(`/leads/${leadUuid}/vertex-proposals`);
            }
            if (response && response.data && response.data.vertex_proposals) {
               this.modalDataList = response.data.vertex_proposals;
            }
         } catch (err) {
            console.error('Error fetching proposals:', err);
            this.$toast.error('Could not load proposals for client.');
         }
         this.modalLoading = false;
      },

      previewContract(contract) {
         // Direct view page or modal
         if (contract.unique_key && contract.internal_key) {
            const routeData = this.$router.resolve({ 
               name: 'clientContract', 
               params: { unique_key: contract.unique_key, access_key: contract.internal_key } 
            });
            window.open(routeData.href, '_blank');
         } else {
            this.$toast.info('This contract cannot be previewed.');
         }
      },

      editContract(contract) {
         if (contract.id) {
            const routeData = this.$router.resolve({ 
               name: 'contract-view', 
               params: { id: contract.id } 
            });
            window.open(routeData.href, '_blank');
         } else {
            this.$toast.info('This contract cannot be edited.');
         }
      },

      previewProposal(proposal) {
         if (proposal.unique_key && proposal.internal_key) {
            const routeData = this.$router.resolve({ 
               name: 'clientProposal', 
               params: { unique_key: proposal.unique_key, access_key: proposal.internal_key } 
            });
            window.open(routeData.href, '_blank');
         } else {
            this.$toast.info('This proposal cannot be previewed.');
         }
      },

      editProposal(proposal) {
         if (proposal.id) {
            const routeData = this.$router.resolve({ 
               name: 'proposal-view', 
               params: { id: proposal.id } 
            });
            window.open(routeData.href, '_blank');
         } else {
            this.$toast.info('This proposal cannot be edited.');
         }
      },

      editClient(client) {
         console.log('Editing client:', client);
         this.$router.push({ name: 'clients-dashboard', query: { organisation_uuid: client.organisation_uuid } });
      },


      getContractStatusName(status) {
         const statuses = {
            0: 'Draft',
            1: 'Sent',
            2: 'Viewed',
            3: 'In Review',
            4: 'Executed',
            5: 'Cancelled',
            6: 'Pending Approval',
            7: 'Approved',
            8: 'Declined'
         };
         return statuses[status] || 'Unknown';
      },
      getContractStatusColor(status) {
         const colors = {
            0: '#6c757d',
            1: '#0ea5e9',
            2: '#a855f7',
            3: '#f59e0b',
            4: '#10b981',
            5: '#ef4444',
            6: '#6366f1',
            7: '#059669',
            8: '#dc2626'
         };
         return colors[status] || '#6c757d';
      },
      getProposalStatusName(status) {
         const statuses = {
            0: 'Draft',
            1: 'Sent',
            2: 'Resent',
            3: 'Accepted',
            4: 'Declined',
            5: 'Expired',
            6: 'Accepted'
         };
         return statuses[status] || 'Unknown';
      },
      getProposalStatusColor(status) {
         const colors = {
            0: '#6c757d',
            1: '#0ea5e9',
            2: '#a855f7',
            3: '#10b981',
            4: '#ef4444',
            5: '#f59e0b',
            6: '#10b981'
         };
         return colors[status] || '#6c757d';
      },
      openMarkAuthModal(org) {
         this.authMarkModal = {
            show: true,
            type: 'mark',
            org: org,
            orgName: org.organisation_name || org.name || 'this organisation',
            isSubmitting: false
         };
      },
      openUnmarkAuthModal(org) {
         this.authMarkModal = {
            show: true,
            type: 'unmark',
            org: org,
            orgName: org.organisation_name || org.name || 'this organisation',
            isSubmitting: false
         };
      },
      closeAuthMarkModal() {
         this.authMarkModal.show = false;
         this.authMarkModal.org = null;
         this.authMarkModal.isSubmitting = false;
      },
      async confirmAuthMarkAction() {
         if (!this.authMarkModal.org) return;
         const isMark = this.authMarkModal.type === 'mark';
         const org = this.authMarkModal.org;
         const uuid = org.organisation_uuid || org.uuid || org.id;
         const orgData = {
            auth_organisation_id: 0,
            has_auth_organisation: isMark ? 2 : 0,
            auth_organisation_name: "",
            auth_organisation_uuid: "",
            xero_contact_id: "",
            marked_auth_creation: isMark
         };

         this.authMarkModal.isSubmitting = true;
         try {
            const response = await this.$axios.put(`/organisations/${uuid}/auth/organisation`, orgData);
            if (response.data.success) {
               this.$toast.success(response.data.message || (isMark ? 'Marked for Auth creation' : 'Unmarked from Auth creation'));
               this.closeAuthMarkModal();
               this.fetchClientsList();
            } else {
               this.$toast.error(response.data.message);
            }
         } catch (error) {
            const msg = error.response && error.response.data && error.response.data.message ? error.response.data.message : `Error ${isMark ? 'marking' : 'unmarking'} organisation`;
            this.$toast.error(msg);
         } finally {
            this.authMarkModal.isSubmitting = false;
         }
      },

      getFormerName(client) {
         if (!client) return '';
         if (client.former_names && client.former_names.length) {
            return client.former_names.join(', ');
         }
         if (client.original_organisation_name && client.original_organisation_name !== client.organisation_name) {
            return client.original_organisation_name;
         }
         if (client.pre_link_snapshot) {
            try {
               const snap = typeof client.pre_link_snapshot === 'string' ? JSON.parse(client.pre_link_snapshot) : client.pre_link_snapshot;
               if (snap && snap.original_organisation_name && snap.original_organisation_name !== client.organisation_name) {
                  return snap.original_organisation_name;
               }
            } catch (e) {
               // ignore
            }
         }
         return '';
      },
      openInAuth(client) {
         if (client.has_auth_organisation == 1 && client.auth_organisation_id) {
            const authBaseUrl = this.$config.getAuthBaseUrl();
            const accessToken = this.$store.state.auth.accessToken;
            const orgId = client.auth_organisation_id;
            const url = `${authBaseUrl}/organisations/${orgId}/settings/?access_token=${accessToken}&org_selected=${orgId}`;
            window.open(url, '_blank');
         } else {
            this.$toast.warning("This organisation is not linked to Auth yet.");
         }
      },
      openInXero(client) {
         if (client.has_auth_organisation == 1) {
            const xeroBaseUrl = process.env.VUE_APP_XERO_BASE_URL || 'https://go.xero.com';
            const shortCode = process.env.VUE_APP_XERO_SHORT_CODE || '!skYq5';
            const contactId = client.xero_contact_id;
            const url = contactId
               ? `${xeroBaseUrl}/app/${shortCode}/contacts/contact/${contactId}`
               : `${xeroBaseUrl}/Organisation/Dashboard`;
            window.open(url, '_blank');
         } else {
            this.$toast.warning("This organisation is not linked to Auth yet.");
         }
      },
      async manualSyncFromAuth(client) {
         if (!client.auth_organisation_id) return;
         this.clientSyncing[client.auth_organisation_id] = true;
         try {
            const response = await this.$axios.post(`/auth/organisations/${client.auth_organisation_id}/sync`);
            if (response.data && response.data.success) {
               this.$toast.success(`Successfully synchronised ${client.organisation_name}!`);
               this.fetchClientsList();
            } else {
               this.$toast.error(response.data.message || "Failed to synchronise organisation.");
            }
         } catch (error) {
            const errMsg = error.response?.data?.message || error.message || "An error occurred during synchronisation.";
            this.$toast.error(`Failed to sync organisation: ${errMsg}`);
         } finally {
            this.clientSyncing[client.auth_organisation_id] = false;
         }
      },
      isPartner(client) {
         if (!client) return false;
         if (client.is_partner || client.isPartner) return true;
         if (client.classifications && Array.isArray(client.classifications)) {
            return client.classifications.some(c => typeof c === 'string' ? c.toLowerCase() === 'partner' : (c.classification && c.classification.toLowerCase() === 'partner'));
         }
         return false;
      },
      isSupplier(client) {
         if (!client) return false;
         if (client.is_supplier || client.isSupplier) return true;
         if (client.classifications && Array.isArray(client.classifications)) {
            return client.classifications.some(c => typeof c === 'string' ? c.toLowerCase() === 'supplier' : (c.classification && c.classification.toLowerCase() === 'supplier'));
         }
         return false;
      },
      getVertexLead(client) {
         if (!client) return '';
         if (client.assigned_user) {
            const name = client.assigned_user.name || `${client.assigned_user.first_name || ''} ${client.assigned_user.last_name || ''}`.trim() || client.assigned_user.email;
            if (name) return name;
         }
         if (client.assigned_user_name) return client.assigned_user_name;
         if (client.assigned_user_id && this.bdms && this.bdms.length > 0) {
            const numId = Number(client.assigned_user_id);
            const matched = this.bdms.find(b => b.auth_user_id === numId || b.id === numId || b.db_id === numId);
            if (matched) {
               return matched.name || `${matched.first_name || ''} ${matched.last_name || ''}`.trim() || matched.email || '';
            }
         }
         return '';
      },
      async fetchBdms() {
         try {
            const res = await this.$axios.get('/vertex-bdm');
            if (res.data && res.data.success && res.data.bdms) {
               this.bdms = res.data.bdms;
            }
         } catch (err) {
            console.error('Error fetching BDMs in ViewClients:', err);
         }
      },
      async openMergeModal(client) {
         this.selectedMergeSource = client;
         this.selectedMergeTarget = null;
         this.mergeReason = '';
         this.mergeConfirmationChecked = false;
         this.showMergeModal = true;
         this.isSearchingMergeTargets = true;

         const currentUuid = client.organisation_uuid || client.uuid || client.id;
         const isSourceAuthSynced = client.has_auth_organisation == 1 || client.is_auth_synced || (client.auth_organisation_id && client.auth_organisation_id > 0);

         const filterEligibleTargets = (list) => {
            return list.filter(item => {
               const itemUuid = item.uuid || item.id || item.organisation_uuid;
               if (itemUuid === currentUuid || item.is_merged) return false;
               if (isSourceAuthSynced) {
                  const isItemAuth = item.has_auth_organisation == 1 || item.is_auth_synced || (item.auth_organisation_id && item.auth_organisation_id > 0);
                  if (!isItemAuth) return false;
               }
               return true;
            }).map(item => ({
               id: item.uuid || item.id || item.organisation_uuid,
               uuid: item.uuid || item.id || item.organisation_uuid,
               name: item.name || item.organisation_name || 'Unnamed Organisation',
               abn: item.abn || '',
               email: item.email || '',
               has_auth_organisation: item.has_auth_organisation,
               is_auth_synced: item.is_auth_synced,
               auth_organisation_id: item.auth_organisation_id
            }));
         };

         if (this.clients && this.clients.length > 0) {
            this.mergeSearchResults = filterEligibleTargets(this.clients);
         }

         try {
            const response = await this.$axios.get('/organisations?pageSize=10000&page=1&excludeTestData=false');
            if (response.data && response.data.organisations) {
               this.mergeSearchResults = filterEligibleTargets(response.data.organisations);
            }
         } catch (err) {
            console.error('Failed to load merge targets:', err);
         } finally {
            this.isSearchingMergeTargets = false;
         }
      },
      closeMergeModal() {
         this.showMergeModal = false;
         this.selectedMergeSource = null;
         this.selectedMergeTarget = null;
         this.mergeSearchResults = [];
      },
      async confirmMerge() {
         if (!this.selectedMergeSource || !this.selectedMergeTarget || !this.mergeConfirmationChecked) return;
         const sourceUuid = this.selectedMergeSource.organisation_uuid || this.selectedMergeSource.uuid || this.selectedMergeSource.id;
         const targetUuid = this.selectedMergeTarget.uuid || this.selectedMergeTarget.id;

         const isSourceAuthSynced = this.selectedMergeSource.has_auth_organisation == 1 || this.selectedMergeSource.is_auth_synced || (this.selectedMergeSource.auth_organisation_id && this.selectedMergeSource.auth_organisation_id > 0);
         const isTargetAuthSynced = this.selectedMergeTarget.has_auth_organisation == 1 || this.selectedMergeTarget.is_auth_synced || (this.selectedMergeTarget.auth_organisation_id && this.selectedMergeTarget.auth_organisation_id > 0);

         if (isSourceAuthSynced && !isTargetAuthSynced) {
            this.$toast.error('An Auth-synced organisation cannot be merged into an unlinked organisation. Destination must also be Auth-synced.');
            return;
         }

         this.isMerging = true;
         try {
            const response = await this.$axios.post(`/organisations/${sourceUuid}/merge-to`, {
               target_organisation_uuid: targetUuid,
               reason: this.mergeReason
            });
            if (response.data && (response.data.success || response.data.status === 'success')) {
               this.$toast.success(response.data.message || 'Organisation merged successfully');
               this.closeMergeModal();
               this.fetchClientsList();
            } else {
               this.$toast.error((response.data && response.data.message) || 'Failed to merge organisation');
            }
         } catch (err) {
            console.error('Error merging organisation:', err);
            const msg = err.response && err.response.data && err.response.data.message ? err.response.data.message : 'Error merging organisation';
            this.$toast.error(msg);
         } finally {
            this.isMerging = false;
         }
      },
      openUnmergeModal(org) {
         this.unmergeTargetOrg = org;
         this.showUnmergeModal = true;
      },
      closeUnmergeModal() {
         this.showUnmergeModal = false;
         this.unmergeTargetOrg = null;
      },
      async confirmUnmerge() {
         if (!this.unmergeTargetOrg) return;
         const targetOrgUuid = this.unmergeTargetOrg.organisation_uuid || this.unmergeTargetOrg.uuid || this.unmergeTargetOrg.id;
         this.isUnmerging = true;
         try {
            const response = await this.$axios.post(`/organisations/${targetOrgUuid}/unmerge`);
            if (response.data && (response.data.success || response.data.status === 'success')) {
               this.$toast.success(response.data.message || 'Organisation unmerged successfully');
               this.closeUnmergeModal();
               this.fetchClientsList();
            } else {
               this.$toast.error((response.data && response.data.message) || 'Failed to unmerge organisation');
            }
         } catch (err) {
            console.error('Error unmerging organisation:', err);
            const msg = err.response && err.response.data && err.response.data.message ? err.response.data.message : 'Error unmerging organisation';
            this.$toast.error(msg);
         } finally {
            this.isUnmerging = false;
         }
      }
   }
};
</script>

<style scoped>
.view-clients-page {
   padding: 10px 0;
}

/* Page Header */
.ms-page-header {
   margin-top: 15px;
   margin-bottom: 24px;
   padding: 20px 24px;
   background: linear-gradient(135deg, #ffffff 0%, #f4f9fb 100%);
   border: 1px solid rgba(0, 130, 152, 0.15);
   border-left: 5px solid #008298;
   border-radius: 12px;
   box-shadow: 0 4px 18px rgba(0, 78, 90, 0.05);
}

.ms-page-header__content {
   display: flex;
   align-items: center;
   gap: 16px;
}

.ms-page-header__icon {
   width: 44px;
   height: 44px;
   border-radius: 10px;
   background: rgba(0, 130, 152, 0.08);
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 1.15rem;
   color: #008298;
}

.ms-page-header__title {
   font-size: 1.45rem;
   font-weight: 800;
   color: #00363f;
   margin: 0;
   letter-spacing: -0.3px;
}

.ms-page-header__subtitle {
   font-size: 0.85rem;
   color: #557d84;
   margin: 4px 0 0;
}

.ms-page-header__breadcrumb {
   font-size: 0.76rem;
   color: #557d84;
   font-weight: 500;
   margin: 0 0 4px;
   letter-spacing: 0.3px;
}

.ms-breadcrumb-sep {
   margin: 0 6px;
   color: rgba(0, 130, 152, 0.3);
}

/* Cards */
.ms-card {
   background: #fff;
   border-radius: 16px;
   box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
   overflow: hidden;
}

.ms-card__header {
   padding: 16px 20px;
   border-bottom: 1px solid #f1f5f9;
   background-color: #f8fafc;
}

.ms-card__title-text {
   font-size: 1.1rem;
   font-weight: 700;
   color: #334155;
   margin: 0;
}

/* Toolbar */
.ms-card__toolbar {
   display: flex;
   align-items: center;
   justify-content: space-between;
   padding: 14px 20px;
   border-bottom: 1px solid #f1f5f9;
   background-color: #fff;
}

.search-input {
   border-radius: 8px !important;
   border: 1.5px solid #cbd5e1;
   height: 38px;
}

.toolbar-label {
   font-size: 0.8rem;
   font-weight: 600;
   color: #64748b;
}

.filter-select {
   border-radius: 8px !important;
   border: 1.5px solid #cbd5e1;
   height: 38px;
   min-width: 110px;
}

/* Pagination */
.ms-pagination {
   display: inline-flex;
   align-items: center;
   border: 1.5px solid #e2e8f0;
   border-radius: 8px;
   background: #f8fafc;
   height: 38px; /* Match search input height exactly */
   box-sizing: border-box;
   overflow: hidden; /* Perfect clipping for button corners */
}

.ms-page-btn {
   background: transparent;
   border: none;
   cursor: pointer;
   color: #00A0C1;
   font-size: 0.8rem;
   padding: 0 12px;
   height: 100%;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   transition: background-color 0.15s;
}

.ms-page-btn:hover:not(:disabled) {
   background: #e6f7fa;
}

.ms-page-btn:disabled {
   color: #cbd5e1;
   cursor: not-allowed;
}

.ms-page-label {
   font-size: 0.8rem;
   color: #64748b;
   font-weight: 500;
   padding: 0 6px;
   white-space: nowrap;
}

/* Table */
.ms-table-wrap {
   overflow-x: auto;
   min-height: 280px; /* Prevent filters/dropdowns from being cut off at the bottom when there are few or no records */
}

.ms-table {
   width: 100%;
   border-collapse: collapse;
}

.ms-table th {
   padding: 13px 20px !important;
   background-color: #f8fafc;
   text-align: left;
   font-size: 0.75rem;
   font-weight: 700;
   color: #475569;
   text-transform: uppercase;
   letter-spacing: 0.5px;
   border-bottom: 2px solid #e2e8f0;
   vertical-align: middle;
}

.ms-table td {
   padding: 14px 20px !important;
   font-size: 0.88rem;
   color: #334155;
   border-bottom: 1px solid #f1f5f9;
   vertical-align: middle;
}

.client-row {
   cursor: pointer;
}

.client-row:hover {
   background-color: #f8fafc;
}

.client-name-cell {
   display: flex;
   align-items: center;
}

.avatar-circle {
   width: 36px;
   height: 36px;
   border-radius: 50%;
   color: white;
   display: flex;
   align-items: center;
   justify-content: center;
   font-weight: 700;
   font-size: 0.9rem;
   text-transform: uppercase;
   border: 1px solid rgba(255, 255, 255, 0.3);
   box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.bg-light-warning {
   background-color: #fff7ed;
   color: #ea580c;
}

.org-test-badge {
   display: inline-flex;
   align-items: center;
   gap: 5px;
   padding: 2px 8px;
   background: #fff7ed;
   color: #ea580c;
   border: 1px solid #ffedd5;
   border-radius: 12px;
   font-size: 0.68rem;
   font-weight: 600;
   white-space: nowrap;
   text-transform: uppercase;
   vertical-align: middle;
}

/* Custom Vertex Brand Switch Styling */
.custom-switch-input:checked {
   background-color: #00A0C1 !important;
   border-color: #00A0C1 !important;
}
.custom-switch-input {
   border-color: #dee2e6 !important;
   transition: transform 0.2s ease, background-color 0.2s ease, border-color 0.2s ease;
}
.custom-switch-input:hover {
   transform: scale(1.05);
}
.custom-switch-input:focus {
   border-color: #00A0C1 !important;
   box-shadow: 0 0 0 0.25rem rgba(0, 160, 193, 0.25) !important;
}

/* Action Circle Buttons */
.action-btn-circle {
   width: 36px;
   height: 36px;
   border-radius: 50% !important;
   border: none !important;
   background-color: #e6f7fa;
   color: #00A0C1;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   font-size: 15px;
   margin: 0 2px;
   transition: transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease, color 0.15s ease;
   outline: none;
   cursor: pointer;
}

.action-btn-circle:hover:not(:disabled) {
   transform: translateY(-1px);
   box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.action-btn-circle:active:not(:disabled) {
   transform: translateY(0);
}

.action-btn-circle--edit,
.action-btn-circle--preview,
.action-btn-circle--mark {
   background-color: #e6f7fa;
   color: #00A0C1;
}

.action-btn-circle--edit:hover:not(:disabled),
.action-btn-circle--preview:hover:not(:disabled),
.action-btn-circle--mark:hover:not(:disabled) {
   background-color: #00A0C1;
   color: #fff;
}

.action-btn-circle--proposals,
.action-btn-circle--open-auth {
   background-color: #f0f9ff;
   color: #3b82f6;
}

.action-btn-circle--proposals:hover:not(:disabled),
.action-btn-circle--open-auth:hover:not(:disabled) {
   background-color: #3b82f6;
   color: #fff;
}

.action-btn-circle--contracts {
   background-color: #f5f3ff;
   color: #7c3aed;
}

.action-btn-circle--contracts:hover:not(:disabled) {
   background-color: #7c3aed;
   color: #fff;
}

.action-btn-circle--open-xero {
   background-color: #ecfdf5;
   color: #16a34a;
}

.action-btn-circle--open-xero:hover:not(:disabled) {
   background-color: #16a34a;
   color: #fff;
}

.action-btn-circle--sync,
.action-btn-circle--merge {
   background-color: #f0fdfa;
   color: #0d9488;
}

.action-btn-circle--sync:hover:not(:disabled),
.action-btn-circle--merge:hover:not(:disabled) {
   background-color: #0d9488;
   color: #fff;
}

.action-btn-circle--restore,
.action-btn-circle--unmerge {
   background-color: #fffbeb;
   color: #f59e0b;
}

.action-btn-circle--restore:hover:not(:disabled),
.action-btn-circle--unmerge:hover:not(:disabled) {
   background-color: #f59e0b;
   color: #fff;
}

.action-btn-circle--unmark,
.action-btn-circle--unlink {
   background-color: #fef2f2;
   color: #ef4444;
}

.action-btn-circle--unmark:hover:not(:disabled),
.action-btn-circle--unlink:hover:not(:disabled) {
   background-color: #ef4444;
   color: #fff;
}

.action-btn-circle:disabled {
   opacity: 0.4 !important;
   cursor: not-allowed !important;
   transform: none !important;
   box-shadow: none !important;
}

/* Modals (Vertex BDM design style) */
.modal-backdrop-custom {
   position: fixed;
   top: 0;
   left: 0;
   width: 100vw;
   height: 100vh;
   background-color: rgba(0, 0, 0, 0.5);
   display: flex;
   justify-content: center;
   align-items: center;
   z-index: 1050;
}

.modal-content-custom {
   position: relative;
   background-color: white;
   padding: 2.5rem 2rem 2rem 2rem;
   border-radius: 16px;
   width: 100%;
   box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

.modal-link-highlight:hover {
   text-decoration: underline !important;
}

.modal-title-custom {
   font-weight: bold;
   font-size: 1.25rem;
   text-align: left;
   margin-bottom: 0;
}

.modal-content-custom hr {
   margin-top: 1rem;
   margin-bottom: 1.5rem;
}

.close-button-custom {
   position: absolute;
   top: 1rem;
   right: 1rem;
   font-size: 2rem;
   font-weight: bold;
   line-height: 1;
   color: #888;
   background: none;
   border: none;
   cursor: pointer;
}

.close-button-custom:hover {
   color: #000;
}

.modal-body-custom {
   max-height: 400px;
   overflow-y: auto;
   padding: 0.5rem 0;
}

/* ── Linked status badges & buttons in Organisation cell ── */
.auth-synced-badge {
   display: inline-flex;
   align-items: center;
   gap: 6px;
   padding: 4px 10px;
   background: #ecfdf5;
   color: #059669;
   border: 1px solid #a7f3d0;
   border-radius: 14px;
   font-size: 0.8125rem;
   font-weight: 600;
   white-space: nowrap;
}

.auth-synced-badge i {
   font-size: 0.78rem;
}

.auth-linked-badge {
   display: inline-flex;
   align-items: center;
   gap: 6px;
   padding: 4px 10px;
   background: #eef2ff;
   color: #4f46e5;
   border: 1px solid #c7d2fe;
   border-radius: 14px;
   font-size: 0.8125rem;
   font-weight: 600;
   white-space: nowrap;
}

.auth-linked-badge i {
   font-size: 0.78rem;
   color: #6366f1;
}

.lead-former-badge {
   display: inline-flex;
   align-items: center;
   gap: 6px;
   padding: 4px 10px;
   background: #fef3c7;
   color: #92400e;
   border: 1px solid #fde68a;
   border-radius: 14px;
   font-size: 0.8125rem;
   font-weight: 600;
   white-space: nowrap;
}

.lead-former-badge i {
   font-size: 0.78rem;
   color: #b45309;
}

.lead-linked-badge {
   display: inline-flex;
   align-items: center;
   gap: 6px;
   padding: 4px 10px;
   background: #ecfdf5;
   color: #059669;
   border: 1px solid #a7f3d0;
   border-radius: 14px;
   font-size: 0.8125rem;
   font-weight: 600;
   white-space: nowrap;
}

.lead-linked-badge i {
   font-size: 0.78rem;
}

.lead-marked-badge {
   display: inline-flex;
   align-items: center;
   gap: 6px;
   padding: 4px 10px;
   background: #eff6ff;
   color: #1d4ed8;
   border: 1px solid #bfdbfe;
   border-radius: 14px;
   font-size: 0.8125rem;
   font-weight: 600;
   white-space: nowrap;
}

.lead-marked-badge i {
   font-size: 0.78rem;
}

.lead-unlinked-btn {
   display: inline-flex;
   align-items: center;
   gap: 6px;
   padding: 4px 10px;
   background: #f8fafc;
   color: #64748b;
   border: 1px dashed #cbd5e1;
   border-radius: 14px;
   font-size: 0.8125rem;
   font-weight: 600;
   white-space: nowrap;
   cursor: pointer;
   transition: all 0.2s ease;
   line-height: 1.35;
}

.lead-unlinked-btn:hover {
   background: #eff6ff;
   color: #2563eb;
   border-color: #93c5fd;
}

.lead-unlinked-btn i {
   font-size: 0.78rem;
}

.org-partner-badge {
   display: inline-flex;
   align-items: center;
   gap: 6px;
   padding: 4px 10px;
   background-color: #ede9fe;
   color: #6d28d9;
   border: 1px solid #ddd6fe;
   border-radius: 14px;
   font-size: 0.8125rem;
   font-weight: 600;
   white-space: nowrap;
   vertical-align: middle;
}

.org-supplier-badge {
   display: inline-flex;
   align-items: center;
   gap: 6px;
   padding: 4px 10px;
   background-color: #e0f2fe;
   color: #0369a1;
   border: 1px solid #bae6fd;
   border-radius: 14px;
   font-size: 0.8125rem;
   font-weight: 600;
   white-space: nowrap;
   vertical-align: middle;
}

.org-partner-badge-sm {
   display: inline-flex;
   align-items: center;
   padding: 2px 7px;
   background-color: #ede9fe;
   color: #6d28d9;
   border: 1px solid #ddd6fe;
   border-radius: 10px;
   font-size: 0.72rem;
   font-weight: 600;
   white-space: nowrap;
}

.org-supplier-badge-sm {
   display: inline-flex;
   align-items: center;
   padding: 2px 7px;
   background-color: #e0f2fe;
   color: #0369a1;
   border: 1px solid #bae6fd;
   border-radius: 10px;
   font-size: 0.72rem;
   font-weight: 600;
   white-space: nowrap;
}

.org-owner-badge {
   display: inline-flex;
   align-items: center;
   gap: 6px;
   padding: 4px 10px;
   background-color: #f1f5f9;
   color: #334155;
   border: 1px solid #cbd5e1;
   border-radius: 14px;
   font-size: 0.8125rem;
   font-weight: 600;
   white-space: nowrap;
   vertical-align: middle;
}

.org-owner-badge i {
   color: #00A0C1;
   font-size: 0.78rem;
}

.org-owner-badge-sm {
   display: inline-flex;
   align-items: center;
   gap: 3px;
   padding: 1px 6px;
   background-color: #f1f5f9;
   color: #334155;
   border: 1px solid #cbd5e1;
   border-radius: 10px;
   font-size: 0.65rem;
   font-weight: 600;
   white-space: nowrap;
}

.org-owner-badge-sm i {
   color: #00A0C1;
   font-size: 0.62rem;
}

.org-merged-badge {
   display: inline-flex;
   align-items: center;
   gap: 4px;
   padding: 2px 7px;
   background-color: #fffbeb;
   color: #92400e;
   border: 1px solid #fde68a;
   border-radius: 12px;
   font-size: 0.68rem;
   font-weight: 600;
   white-space: nowrap;
}

.org-merged-badge i {
   color: #d97706;
   font-size: 0.65rem;
}

.org-merged-badge-sm {
   display: inline-flex;
   align-items: center;
   gap: 3px;
   padding: 1px 6px;
   background-color: #fffbeb;
   color: #92400e;
   border: 1px solid #fde68a;
   border-radius: 10px;
   font-size: 0.65rem;
   font-weight: 600;
   white-space: nowrap;
}

.org-merged-badge-sm i {
   color: #d97706;
   font-size: 0.6rem;
}

.auth-org-subtext {
   display: inline-flex;
   align-items: center;
   font-size: 0.75rem;
   color: #475569;
   font-weight: 500;
}

/* Modal Overlay & Card custom styles for link modal */
.lead-modal-overlay {
   position: fixed;
   top: 0;
   left: 0;
   right: 0;
   bottom: 0;
   background-color: rgba(15, 23, 42, 0.4);
   display: flex;
   align-items: center;
   justify-content: center;
   z-index: 1050;
   backdrop-filter: blur(4px);
}

.lead-modal {
   width: 90%;
   max-width: 500px;
   background: #fff;
   border-radius: 16px;
   box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
   overflow: hidden;
}

.lead-modal__header {
   padding: 16px 20px;
   display: flex;
   justify-content: space-between;
   align-items: center;
   background: #ffffff;
}

.lead-modal__title {
   margin: 0;
   font-size: 1.1rem;
   font-weight: 600;
   color: #1e293b;
   display: flex;
   align-items: center;
   gap: 8px;
}

.lead-modal__close {
   background: transparent;
   border: none;
   font-size: 1.5rem;
   color: #64748b;
   cursor: pointer;
   line-height: 1;
   padding: 0;
   transition: color 0.15s ease;
}

.lead-modal__close:hover {
   color: #ef4444;
}

.lead-modal__divider {
   height: 1px;
   background: #e2e8f0;
}

.lead-modal__body {
   padding: 20px;
}

.lead-form-label {
   display: block;
   font-weight: 500;
   font-size: 0.875rem;
   color: #475569;
   margin-bottom: 8px;
}

.lead-modal__actions {
   display: flex;
   justify-content: flex-end;
   gap: 8px;
   margin-top: 24px;
}

.lead-btn {
   padding: 10px 22px;
   border-radius: 10px;
   font-size: 0.875rem;
   font-weight: 600;
   cursor: pointer;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   gap: 6px;
   transition: all 0.2s ease;
   border: none;
}

.lead-btn--primary {
   background: linear-gradient(135deg, #00A0C1, #33b5d1);
   color: #fff;
   box-shadow: 0 2px 8px rgba(0, 160, 193, 0.3);
}

.lead-btn--primary:hover {
   background: linear-gradient(135deg, #008da8, #00A0C1);
   box-shadow: 0 4px 16px rgba(0, 160, 193, 0.4);
   transform: translateY(-1px);
}

.lead-btn--primary:disabled {
   opacity: 0.5;
   cursor: not-allowed;
   transform: none;
}

/* ── Modal Transition ── */
.lead-modal-fade-enter-active,
.lead-modal-fade-leave-active {
   transition: opacity 0.2s ease;
}

.lead-modal-fade-enter-from,
.lead-modal-fade-leave-to {
   opacity: 0;
}

/* Premium Tab Navigation */
.org-tabs-container {
   background-color: #f8fafc;
   border-bottom: 1.5px solid #e2e8f0;
   padding: 0 24px;
   display: flex;
   justify-content: space-between;
   align-items: center;
   flex-wrap: wrap;
   gap: 16px;
}

.header-search-container {
   position: relative;
   width: 320px;
   margin: 8px 0;
}

.header-search-input-wrapper {
   position: relative;
   display: flex;
   align-items: center;
}

.header-search-input-wrapper .search-icon {
   position: absolute;
   left: 12px;
   color: #94a3b8;
   font-size: 0.9rem;
   pointer-events: none;
}

.header-search-input {
   width: 100%;
   padding: 8px 36px 8px 36px;
   font-size: 0.85rem;
   border: 1.5px solid #cbd5e1;
   border-radius: 8px;
   color: #334155;
   background-color: #fff;
   transition: all 0.2s ease;
   height: 38px;
   outline: none;
}

.header-search-input:focus {
   border-color: #00A0C1;
   box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.15);
}

.clear-search-btn {
   position: absolute;
   right: 12px;
   background: none;
   border: none;
   color: #94a3b8;
   cursor: pointer;
   padding: 4px;
   display: flex;
   align-items: center;
   justify-content: center;
   border-radius: 50%;
   transition: all 0.2s ease;
}

.clear-search-btn:hover {
   color: #64748b;
   background-color: #f1f5f9;
}

/* Dropdown overlay */
.header-search-dropdown {
   position: absolute;
   top: 100%;
   left: 0;
   right: 0;
   background-color: #fff;
   border: 1.5px solid #e2e8f0;
   border-radius: 8px;
   box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
   z-index: 1000;
   max-height: 250px;
   overflow-y: auto;
   margin-top: 6px;
}

.header-search-dropdown-item {
   padding: 10px 14px;
   cursor: pointer;
   transition: background-color 0.15s ease;
}

.header-search-dropdown-item:hover {
   background-color: #f1f5f9;
}

.org-name-text {
   font-weight: 600;
   color: #1e293b;
   font-size: 0.85rem;
}

.org-type-badge {
   font-size: 0.7rem;
   font-weight: 700;
   padding: 3px 8px;
   border-radius: 6px;
   background-color: #f8fafc;
   color: #64748b;
   border: 1px solid #e2e8f0;
   text-transform: uppercase;
   letter-spacing: 0.5px;
   display: inline-flex;
   align-items: center;
   white-space: nowrap;
}

.org-type-badge--lead {
   background-color: #dcfce7;
   color: #15803d;
   border-color: #bbf7d0;
}

.org-type-badge--client,
.org-type-badge--organisation {
   background-color: #ffedd5;
   color: #c2410c;
   border-color: #fed7aa;
}

.org-type-badge--prospect {
   background-color: #fef3c7;
   color: #b45309;
   border-color: #fde68a;
}

.org-type-badge--partner {
   background-color: #ede9fe;
   color: #6d28d9;
   border-color: #ddd6fe;
}

.org-type-badge--supplier {
   background-color: #e0f2fe;
   color: #0369a1;
   border-color: #bae6fd;
}

.org-type-badge--merged {
   background-color: #fffbeb;
   color: #92400e;
   border-color: #fde68a;
}

.org-type-badge--deactivated {
   background-color: #fee2e2;
   color: #dc2626;
   border-color: #fecaca;
}

.org-tabs-nav {
   display: flex;
   gap: 4px;
}

.org-tab-btn {
   background: none;
   border: none;
   padding: 16px 20px;
   font-weight: 600;
   color: #64748b;
   cursor: pointer;
   position: relative;
   transition: all 0.2s ease;
   font-size: 0.9rem;
   outline: none;
}

.org-tab-btn:hover {
   color: #00768a;
}

.org-tab-btn.active {
   color: #00768a;
   font-weight: 700;
}

.org-tab-btn.active::after {
   content: '';
   position: absolute;
   bottom: 0;
   left: 0;
   right: 0;
   height: 3px;
   background-color: #00768a;
   border-radius: 3px 3px 0 0;
}

.header-filter-dropdown-wrapper {
   position: relative;
   display: inline-block;
}

.header-filter-btn {
   background: transparent;
   border: none;
   cursor: pointer;
   color: #94a3b8;
   padding: 4px 8px;
   border-radius: 6px;
   font-size: 0.85rem;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   transition: all 0.2s ease;
}

.header-filter-btn:hover {
   color: #475569;
   background-color: #e2e8f0;
}

.header-filter-btn.filter-active {
   color: #00A0C1;
   background-color: #e6f7fa;
}

.header-filter-menu {
   position: absolute;
   top: 100%;
   right: 0;
   margin-top: 6px;
   background-color: #fff;
   border: 1.5px solid #e2e8f0;
   border-radius: 8px;
   box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
   z-index: 1050;
   min-width: 160px;
   padding: 6px 0;
   display: flex;
   flex-direction: column;
   text-align: left;
}

.header-filter-option {
   padding: 8px 16px;
   font-size: 0.8rem;
   font-weight: 500;
   color: #475569;
   cursor: pointer;
   transition: background-color 0.15s ease;
   text-transform: none;
   white-space: nowrap;
   display: block;
   width: 100%;
}

.header-filter-option:hover {
   background-color: #f1f5f9;
   color: #1e293b;
}

.header-filter-option.active {
   background-color: #e6f7fa;
   color: #00768a;
   font-weight: 600;
}

.btn-filter-active {
   border-color: #00A0C1 !important;
   color: #00A0C1 !important;
   background-color: #e6f7fa !important;
}

.filter-count-badge {
   display: inline-flex;
   align-items: center;
   justify-content: center;
   background-color: #00A0C1;
   color: #fff;
   border-radius: 10px;
   font-size: 0.7rem;
   padding: 1px 6px;
   font-weight: 700;
   line-height: 1.2;
}

.filter-modal-radio-card {
   display: flex;
   align-items: center;
   padding: 10px 14px;
   border: 1.5px solid #e2e8f0;
   border-radius: 8px;
   cursor: pointer;
   transition: all 0.2s ease;
   font-size: 0.88rem;
   font-weight: 500;
   color: #334155;
   margin-bottom: 0;
}

.filter-modal-radio-card:hover {
   background-color: #f8fafc;
   border-color: #cbd5e1;
}

.filter-modal-radio-card.selected {
   background-color: #e6f7fa;
   border-color: #00A0C1;
   color: #00768a;
}

.filter-modal-radio-card input[type="radio"] {
   cursor: pointer;
   margin-top: 0;
}

/* Vertex Theme Buttons */
.btn-vertex-primary {
   background-color: #00A0C1 !important;
   border: 1px solid #00A0C1 !important;
   color: #ffffff !important;
   font-weight: 600 !important;
   border-radius: 6px !important;
   transition: all 0.2s ease-in-out !important;
}

.btn-vertex-primary:hover,
.btn-vertex-primary:focus {
   background-color: #008298 !important;
   border-color: #008298 !important;
   color: #ffffff !important;
   box-shadow: 0 2px 8px rgba(0, 160, 193, 0.35) !important;
   transform: translateY(-1px);
}

.btn-vertex-primary:disabled {
   background-color: #a0d8e6 !important;
   border-color: #a0d8e6 !important;
   color: #ffffff !important;
   opacity: 0.65 !important;
   transform: none !important;
   box-shadow: none !important;
   cursor: not-allowed !important;
}

.lead-modal .form-select:focus {
   border-color: #00A0C1 !important;
   box-shadow: 0 0 0 0.2rem rgba(0, 160, 193, 0.25) !important;
}

/* Vertex Checkbox Component */
.vertex-checkbox-card {
   background: #f8fafc;
   border: 1px solid #e2e8f0;
   cursor: pointer;
   transition: all 0.2s ease-in-out;
}

.vertex-checkbox-card:hover {
   background: #f1f5f9;
   border-color: #cbd5e1;
}

.vertex-checkbox-card--checked {
   background: rgba(0, 160, 193, 0.08) !important;
   border-color: rgba(0, 160, 193, 0.5) !important;
   box-shadow: 0 1px 3px rgba(0, 160, 193, 0.1);
}

.vertex-checkbox-input {
   width: 18px !important;
   height: 18px !important;
   margin-top: 0 !important;
   cursor: pointer !important;
   border: 1.5px solid #94a3b8 !important;
   border-radius: 4px !important;
   accent-color: #00A0C1 !important;
   transition: all 0.15s ease-in-out !important;
}

.vertex-checkbox-input:checked {
   background-color: #00A0C1 !important;
   border-color: #00A0C1 !important;
}
</style>

<style>
/* Merge Target VueMultiselect Styling (Unscoped for VueMultiselect dropdown) */
.merge-target-multiselect .multiselect__option {
   background: #ffffff !important;
   color: #1e293b !important;
   padding: 8px 14px !important;
   min-height: auto !important;
}

.merge-target-multiselect .multiselect__option::after {
   display: none !important;
}

.merge-target-multiselect .merge-option-name {
   color: #1e293b !important;
   font-weight: 600;
   transition: color 0.15s ease;
}

.merge-target-multiselect .merge-option-sub {
   color: #64748b !important;
   transition: color 0.15s ease;
}

.merge-target-multiselect .merge-auth-badge {
   transition: all 0.15s ease;
}

/* Highlighted / Hovered / Keyboard Active Option */
.merge-target-multiselect .multiselect__option--highlight {
   background: #00A0C1 !important;
   color: #ffffff !important;
}

.merge-target-multiselect .multiselect__option--highlight::after {
   background: #00A0C1 !important;
   color: #ffffff !important;
}

.merge-target-multiselect .multiselect__option--highlight *,
.merge-target-multiselect .multiselect__option--highlight .merge-option-name {
   color: #ffffff !important;
}

.merge-target-multiselect .multiselect__option--highlight .merge-option-sub {
   color: rgba(255, 255, 255, 0.92) !important;
}

.merge-target-multiselect .multiselect__option--highlight .merge-auth-badge,
.merge-target-multiselect .multiselect__option--highlight .badge {
   background-color: rgba(255, 255, 255, 0.25) !important;
   color: #ffffff !important;
   border-color: rgba(255, 255, 255, 0.5) !important;
}

.merge-target-multiselect .multiselect__option--highlight .merge-auth-badge i,
.merge-target-multiselect .multiselect__option--highlight .badge i {
   color: #ffffff !important;
}

/* Selected Option (Normal state) */
.merge-target-multiselect .multiselect__option--selected {
   background: #e6f4f6 !important;
   color: #00768a !important;
   font-weight: 700 !important;
}

.merge-target-multiselect .multiselect__option--selected::after {
   background: #e6f4f6 !important;
   color: #00768a !important;
}

.merge-target-multiselect .multiselect__option--selected .merge-option-name {
   color: #00768a !important;
}

.merge-target-multiselect .multiselect__option--selected .merge-option-sub {
   color: #00768a !important;
}

.merge-target-multiselect .multiselect__option--selected .merge-auth-badge,
.merge-target-multiselect .multiselect__option--selected .badge {
   background-color: rgba(0, 118, 138, 0.12) !important;
   color: #00768a !important;
   border-color: rgba(0, 118, 138, 0.25) !important;
}

/* Selected AND Highlighted Option */
.merge-target-multiselect .multiselect__option--selected.multiselect__option--highlight {
   background: #008fae !important;
   color: #ffffff !important;
}

.merge-target-multiselect .multiselect__option--selected.multiselect__option--highlight::after {
   background: #008fae !important;
   color: #ffffff !important;
}

.merge-target-multiselect .multiselect__option--selected.multiselect__option--highlight *,
.merge-target-multiselect .multiselect__option--selected.multiselect__option--highlight .merge-option-name {
   color: #ffffff !important;
}

.merge-target-multiselect .multiselect__option--selected.multiselect__option--highlight .merge-option-sub {
   color: rgba(255, 255, 255, 0.92) !important;
}

.merge-target-multiselect .multiselect__option--selected.multiselect__option--highlight .merge-auth-badge,
.merge-target-multiselect .multiselect__option--selected.multiselect__option--highlight .badge {
   background-color: rgba(255, 255, 255, 0.25) !important;
   color: #ffffff !important;
   border-color: rgba(255, 255, 255, 0.5) !important;
}

.merge-target-multiselect .multiselect__option--selected.multiselect__option--highlight .merge-auth-badge i,
.merge-target-multiselect .multiselect__option--selected.multiselect__option--highlight .badge i {
   color: #ffffff !important;
}
</style>
